// PROVENANCE: copied from ptc-statetax 27fcfb4, src/components/moments/useMoment.ts (the fleet primitive; spec there,
// conversions' moments in docs/accomplishment-moments.md). Deviations: imports from conversionsApi.
import { useContext, useEffect, useRef } from 'react';
import { useSaveMomentPreferences, useUserPreferences } from '../../services/conversionsApi';
import {
  MAX_SEEN_MOMENTS, MomentContext, markSeenLocally, momentSettings, seenLocally, type Moment,
} from './moments';

/**
 * Fires each moment at most ONCE per person per `moment.id`, and never when the person turned moments
 * off. Pass `null` (or an empty list) when no fact holds. Callers memoize the argument (it is a
 * dependency). A list fires in order (the host queues them) and is recorded as seen in ONE save, so
 * two moments landing together never race two writes of the seen record.
 *
 * Waits for the preferences to load before deciding anything: firing first and checking later is
 * how a person who switched moments off still gets one, and how "seen" fires twice.
 */
export function useMoment(moments: Moment | Moment[] | null): void {
  const { show } = useContext(MomentContext);
  const { data: prefs, isSuccess } = useUserPreferences();
  const { mutate: saveMoments } = useSaveMomentPreferences();
  const fired = useRef(new Set<string>());

  useEffect(() => {
    const list = moments === null ? [] : Array.isArray(moments) ? moments : [moments];
    if (list.length === 0 || !isSuccess) return;
    const settings = momentSettings(prefs);
    if (!settings.enabled) return;
    const fresh = list.filter((m) =>
      !settings.seen.includes(m.id) && !fired.current.has(m.id) && !seenLocally(m.id));
    if (fresh.length === 0) return;

    for (const m of fresh) {
      fired.current.add(m.id);
      markSeenLocally(m.id);
      show(m);
    }
    saveMoments({ enabled: true, seen: [...settings.seen, ...fresh.map((m) => m.id)].slice(-MAX_SEEN_MOMENTS) });
  }, [moments, isSuccess, prefs, show, saveMoments]);
}
